import {v4 as uuidv4} from "uuid";
import {faker}  from "@faker-js/faker";
import {
  getAccounts,
  getSelectedDepartmentFromAccount,
} from "../helpers/get-accounts";
import { getOrgApp } from "../helpers/get-orgApp";
import {
  SECURITY_BOT_ID,
  COMPLIANCE_BOT_ID,
  BOT_TYPE_SECURITY,
  BOT_TYPE_COMPLIANCE,
  NOT_CONFIGURED,
  ACTIVE,
} from "../../utils/app-constants.json";
import { getBotType } from "../../modules/day2-bots/utils";
import securityBotManifest from "./bot-manifest/security-bot-manifest.json";
import complianceBotManifest from "./bot-manifest/compliance-bot-manifest.json";
import {
  SECURITY_BOT_INSIGHT_COUNT,
  COMPLIANCE_BOT_INSIGHT_COUNT,
} from "./constants";
import { getStorage, setStorage } from "../helpers/storage";
import { isArrayWithLength } from "../../utils/array-methods";

const bots = [
  {
    Name: "Security Bot",
    Description:
      "Checks for 200+ security best practices on AWS resources & services. Helps verify alignment with NIST CSF, NIST 800-53, AICPA TSC and ISO 27001:2013 standards.",
    IconType: "securityBot",
    Id: SECURITY_BOT_ID,
    BotManifest: securityBotManifest,
    InsightCount: SECURITY_BOT_INSIGHT_COUNT,
  },
  {
    Name: "Compliance Bot",
    Description:
      "Detects, reports and helps remediate cloud resources and server application compliance across on-premise and public cloud environments.",
    IconType: "complianceBot",
    Id: COMPLIANCE_BOT_ID,
    BotManifest: complianceBotManifest,
    InsightCount: COMPLIANCE_BOT_INSIGHT_COUNT,
  },
  {
    Name: "Operations Bot",
    Description:
      "Automate routine operations using DAY2\u2122 tasks, custom tasks, and external web hooks.",
    IconType: "opsBot",
  },
  {
    Name: "Backup Bot",
    Description:
      "Protect EBS Volumes, RDS, DynamoDB databases, AWS Storage Gateway, and EFS with automated backups.",
    IconType: "backupBot",
  },
  {
    Name: "Cost Monitor Bot",
    Description:
      "Monitors AWS bills and usage against set budgets, detect and reports  cost anomalies, budget risks and overage.",
    IconType: "costBot",
  },
  {
    Name: "Anomaly Watch Bot",
    Description:
      "Detects and reports anamalous patterns AWS resource and service metrics for anomalous.",
    IconType: "anamolyBot",
  },
  {
    Name: "Tagging Bot",
    Description:
      "Enforces tag key/values for AWS resources across multiple accounts and across regions.",
    IconType: "taggingBot",
  },
];

var regionCodes = [
  "us-east-1",
  "us-east-2",
  "us-west-1",
  "us-west-2",
  "ap-south-1",
];

export const getAllBots = async () => {
  let botsList = [];
  let orgAppDetails = await getOrgApp();
  let accounts = await getAccounts();
  let AccountNumber = [faker.helpers.arrayElement(accounts)];
  let RegionCode = faker.helpers.arrayElement(regionCodes);
  let Department = getSelectedDepartmentFromAccount(
    orgAppDetails,
    AccountNumber
  );
  let Targets = [
    {
      DepartmentId: Department.map((dept) => dept.deptId)[0],
      AccountNumber: AccountNumber[0],
      Regions: [RegionCode],
    },
  ];
  const instances = [];

  bots.forEach((bot) => {
    const failedCount = Math.round(bot.InsightCount / 3);
    const passedCount = bot.InsightCount - failedCount;
    const botStatus = [
      "Security Bot",
      "Compliance Bot",
      "Cost Monitor Bot",
      "Tagging Bot",
    ].includes(bot.Name)
      ? NOT_CONFIGURED
      : ACTIVE;
    const botData = {
      Description: bot.Description,
      Name: bot.Name,
      IconType: bot.IconType,
      Status: botStatus,
      Targets: botStatus === ACTIVE ? Targets : [],
      Instances: instances,
      BotManifest: bot.BotManifest,
      Insights:
        botStatus === ACTIVE
          ? {
              Passed:
                passedCount ||
                faker.number.int({
                  min: 25,
                  max: 70,
                }),
              Failed:
                failedCount ||
                faker.number.int({
                  min: 0,
                  max: 5,
                }),
            }
          : {},
    };
    botData.Id = bot.Id ? bot.Id : uuidv4();
    botsList.push(botData);
  });
  return botsList;
};

export const getBotManifest = async (id) => {
  const data = await getStorage("LIST_BOTS");
  let bot;
  data.forEach((manifest) => {
    if (manifest.Id === id) {
      bot = {
        ...manifest.BotManifest,
        Id: id,
      };
    }
  });
  return bot;
};

export const getBotDetails = async (id) => {
  const data = await getStorage("LIST_BOTS");
  return (
    data && Array.isArray(data) && data.find((details) => details.Id === id)
  );
};

export const configureBot = async (id, botData) => {
  const botList = await getStorage("LIST_BOTS");
  const updatedBots =
    Array.isArray(botList) &&
    botList.map((bot) => {
      if (bot && bot.Id === id) {
        const { ManifestContent, Targets } = botData;
        return {
          ...bot,
          BotManifest: ManifestContent,
          Targets,
          Status: ACTIVE,
        };
      }
      return bot;
    });
  await setStorage("LIST_BOTS", updatedBots);
};

const preRequisites = [
  {
    Message: "VPC count should be less that five in a region",
    type: BOT_TYPE_SECURITY,
  },
  {
    Message: "VPC CIDR range should be with in this range",
    type: BOT_TYPE_SECURITY,
  },
  {
    Message:
      "AWS Config rule count should be less than 250 per account per region",
    type: BOT_TYPE_COMPLIANCE,
  },
];

export const getPreRequisites = (id) => {
  const botType = getBotType(id);
  return (
    preRequisites &&
    Array.isArray(preRequisites) &&
    preRequisites
      .filter((check) => check.type === botType)
      .map((check) => check.Message)
  );
};

export const getBotConfig = async (id) => {
  const bots = await getStorage("LIST_BOTS");
  const bot = bots.find((bot) => bot.Id === id);
  return {
    ManifestContent: bot ? bot.BotManifest : {},
    Targets: bot ? bot.Targets : [],
  };
};

export const offBoardBot = async (id, targets) => {
  const bots = await getStorage("LIST_BOTS");
  const index = bots.findIndex((bot) => bot.Id === id);
  if (index < -1) {
    return;
  }
  const botTarges = bots[index].Targets;
  if (isArrayWithLength(botTarges) && isArrayWithLength(targets)) {
    targets.forEach((target) => {
      const targetIndex = botTarges.findIndex(
        (ele) => ele.AccountNumber === target.AccountNumber
      );
      if (botTarges[targetIndex].Regions.length === target.Regions.length) {
        botTarges.splice(targetIndex, 1);
      } else {
        target.Regions.forEach((reg) => {
          const regIndex = botTarges[targetIndex].Regions.findIndex(
            (r) => r === reg
          );
          botTarges[targetIndex].Regions.splice(regIndex, 1);
        });
      }
    });
    if (!isArrayWithLength(botTarges)) {
      bots[index].Status = NOT_CONFIGURED;
    }
    await setStorage("LIST_BOTS", bots);
  }
};
